import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

const KEY = "unicorp_cookie_consent";

// Lightweight, self-contained cookie/consent banner (localStorage only — no tracking).
export default function CookieConsent() {
  const [show, setShow] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    try { if (!localStorage.getItem(KEY)) setShow(true); } catch { /* ignore */ }
    const open = () => setShow(true);
    window.addEventListener("unicorp:cookie-preferences", open);
    return () => window.removeEventListener("unicorp:cookie-preferences", open);
  }, []);

  const decide = (value) => {
    try { localStorage.setItem(KEY, value); } catch { /* ignore */ }
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:max-w-md z-[60]"
          data-testid="cookie-consent"
        >
          <div className="rounded-xl border border-border bg-card shadow-[0_20px_60px_-20px_rgb(7,22,51,0.35)] p-5">
            <div className="flex items-start gap-3">
              <span className="grid place-items-center h-9 w-9 rounded-sm bg-secondary text-orange-600 shrink-0"><Cookie className="h-5 w-5" /></span>
              <div>
                <p className="text-sm font-medium text-foreground">{t("cookie.title")}</p>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                  {t("cookie.body")}{" "}
                  <Link to="/legal/privacy" className="text-primary underline underline-offset-2 hover:text-orange-700" data-testid="cookie-privacy-link">{t("cookie.privacyPolicy")}</Link>.
                </p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button variant="ghost" size="sm" className="h-9 text-muted-foreground hover:text-foreground" onClick={() => decide("necessary")} data-testid="cookie-decline">{t("cookie.necessaryOnly")}</Button>
              <Button size="sm" className="h-9 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 px-5" onClick={() => decide("accepted")} data-testid="cookie-accept">{t("cookie.accept")}</Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
